import React, { useState, useRef, useEffect } from 'react';
import ColorField from './ColorField';
import ConfirmDialog from './ConfirmDialog';
import './PromptBar.css';

// Icon mapping for each field type
const FIELD_ICONS = {
  for: { normal: '/prompt_icon/for-icon.svg', hover: '/prompt_icon/for-icon-hover.svg' },
  color: { normal: '/prompt_icon/color-icon.svg', hover: '/prompt_icon/color-icon-hover.svg' },
  style: { normal: '/prompt_icon/style-icon.svg', hover: '/prompt_icon/style-icon-hover.svg' },
  typography: { normal: '/prompt_icon/font-icon.svg', hover: '/prompt_icon/font-icon-hover.svg' },
  size: { normal: '/prompt_icon/size-icon.svg', hover: '/prompt_icon/size-icon-hover.svg' },
  mood: { normal: '/prompt_icon/mood-icon.svg', hover: '/prompt_icon/mood-icon-hover.svg' },
  layout: { normal: '/prompt_icon/layout-icon.svg', hover: '/prompt_icon/layout-icon-hover.svg' },
  material: { normal: '/prompt_icon/material-icon.svg', hover: '/prompt_icon/material-icon-hover.svg' },
  dynamic_input: { normal: '/prompt_icon/dynamic-input-icon.svg', hover: '/prompt_icon/dynamic-input-icon-hover.svg' },
  assets: { normal: '/prompt_icon/asset-icon.svg', hover: '/prompt_icon/asset-icon-hover.svg' }
};

const PromptBar = ({
  fields = [],
  formData = {},
  onRegenerate,
  assets = []
}) => {
  const [hoveredField, setHoveredField] = useState(null);
  const [expandedField, setExpandedField] = useState(null);
  const [editedValues, setEditedValues] = useState({ ...formData, assets: assets });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [assetPreviews, setAssetPreviews] = useState(() => {
    return (assets || []).map(asset => {
      if (typeof asset === 'string') return asset;
      if (asset instanceof File) return URL.createObjectURL(asset);
      if (typeof asset === 'object' && asset !== null && asset.url) return asset.url;
      return null;
    }).filter(Boolean);
  });
  const fileInputRef = useRef(null);
  const barRef = useRef(null);
  const inputRef = useRef(null);

  // Create asset field for display
  const assetField = {
    id: 'assets',
    display: 'Asset Images',
    type: 'file',
    placeholder: 'Upload reference images...'
  };

  // Combine fields with asset field
  const allFields = [...fields, assetField];

  // Close expanded view when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) {
        setExpandedField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when expanded
  useEffect(() => {
    if (expandedField && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 400);
    }
  }, [expandedField]);

  // Sync editedValues when formData or assets change
  useEffect(() => {
    setEditedValues(prev => ({ ...prev, ...formData, assets: assets }));
    setAssetPreviews(
      (assets || []).map(asset => {
        if (typeof asset === 'string') return asset;
        if (asset instanceof File) return URL.createObjectURL(asset);
        if (typeof asset === 'object' && asset !== null && asset.url) return asset.url;
        return null;
      }).filter(Boolean)
    );
  }, [formData, assets]);

  // Get icon for a field
  const getIcon = (fieldId, isHovered) => {
    const icons = FIELD_ICONS[fieldId] || FIELD_ICONS.dynamic_input;
    return isHovered ? icons.hover : icons.normal;
  };

  // Get display value for a field
  const getDisplayValue = (field) => {
    if (field.id === 'assets') {
      const assetCount = editedValues.assets?.length || 0;
      return assetCount > 0 ? `${assetCount} image${assetCount > 1 ? 's' : ''}` : null;
    }
    const value = editedValues[field.id];
    if (!value) return null;
    const displayStr = Array.isArray(value) ? value.join(', ') : String(value);
    return displayStr.length > 20 ? displayStr.substring(0, 20) + '...' : displayStr;
  };

  // Handle file upload for assets
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newAssets = [...(editedValues.assets || []), ...files];
      setEditedValues(prev => ({ ...prev, assets: newAssets }));
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setAssetPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  // Remove an asset
  const handleRemoveAsset = (index) => {
    setEditedValues(prev => ({
      ...prev,
      assets: prev.assets.filter((_, i) => i !== index)
    }));
    setAssetPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Get preview URL for asset
  const getAssetPreviewUrl = (asset, index) => {
    if (assetPreviews[index]) return assetPreviews[index];
    if (typeof asset === 'string') return asset;
    if (asset instanceof File) return URL.createObjectURL(asset);
    return null;
  };

  // Handle value change
  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setEditedValues(prev => ({ ...prev, [name]: value }));
  };

  // Handle submit
  const handleSubmit = () => {
    setShowConfirmDialog(true);
  };

  // Confirm generation
  const handleConfirmGenerate = () => {
    setShowConfirmDialog(false);
    if (onRegenerate) {
      const normalizedValues = { ...editedValues };
      if (normalizedValues.assets && Array.isArray(normalizedValues.assets)) {
        normalizedValues.assets = normalizedValues.assets.map(asset => {
          if (asset instanceof File) {
            return asset;
          } else if (typeof asset === 'object' && asset !== null && asset.url) {
            return asset.url;
          } else if (typeof asset === 'string') {
            return asset;
          }
          return asset;
        }).filter(Boolean);
      }
      onRegenerate(normalizedValues);
    }
    setExpandedField(null);
  };

  // Cancel generation
  const handleCancelGenerate = () => {
    setShowConfirmDialog(false);
  };

  // Handle icon click
  const handleIconClick = (field) => {
    if (expandedField?.id === field.id) {
      setExpandedField(null);
    } else {
      setExpandedField(field);
    }
  };

  // Clear a value
  const handleClearValue = (fieldId) => {
    setEditedValues(prev => ({ ...prev, [fieldId]: '' }));
  };

  const isExpanded = !!expandedField;

  // Check if options panel should show
  const showOptionsPanel = isExpanded && (
    expandedField.id === 'assets' ||
    expandedField.type === 'smart_dropdown' ||
    expandedField.type === 'dropdown' ||
    expandedField.type === 'color'
  );

  // Render options panel content
  const renderOptionsPanel = () => {
    if (!expandedField) return null;

    if (expandedField.id === 'assets') {
      return (
        <div className="assets-panel">
          {editedValues.assets && editedValues.assets.length > 0 && (
            <div className="assets-preview-grid">
              {editedValues.assets.map((asset, index) => (
                <div key={index} className="asset-preview-item" style={{ '--i': index }}>
                  <img
                    src={getAssetPreviewUrl(asset, index)}
                    alt={`Asset ${index + 1}`}
                    className="asset-preview-img"
                  />
                  <button
                    type="button"
                    className="asset-remove-btn"
                    onClick={() => handleRemoveAsset(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="asset-upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="upload-icon">📎</span>
            {editedValues.assets?.length > 0 ? 'Add more images' : 'Upload images'}
          </button>
        </div>
      );
    }

    if (expandedField.type === 'smart_dropdown' || expandedField.type === 'dropdown') {
      return (
        <div className="options-grid">
          {(expandedField.options || []).map((option) => (
            <div
              key={option}
              className={`option-item ${editedValues[expandedField.id] === option ? 'selected' : ''}`}
              onClick={() => handleValueChange({ target: { name: expandedField.id, value: option } })}
            >
              {option}
            </div>
          ))}
        </div>
      );
    }

    if (expandedField.type === 'color') {
      return (
        <div className="color-input-panel">
          <ColorField
            id={expandedField.id}
            display={expandedField.display}
            placeholder={expandedField.placeholder}
            value={Array.isArray(editedValues[expandedField.id]) 
              ? editedValues[expandedField.id] 
              : (editedValues[expandedField.id] || [])}
            onChange={handleValueChange}
            placement="up"
          />
        </div>
      );
    }

    return null;
  };

  // Render input content
  const renderInputContent = () => {
    if (!expandedField) return null;

    if (expandedField.id === 'assets') {
      return editedValues.assets?.length > 0 ? (
        <div className="value-chip">
          <span className="chip-text">{editedValues.assets.length} image{editedValues.assets.length > 1 ? 's' : ''}</span>
        </div>
      ) : (
        <span className="input-placeholder">Click to upload images...</span>
      );
    }

    if (expandedField.type === 'smart_dropdown') {
      return (
        <input
          ref={inputRef}
          type="text"
          id={expandedField.id}
          name={expandedField.id}
          value={Array.isArray(editedValues[expandedField.id])
            ? editedValues[expandedField.id].join(', ')
            : (editedValues[expandedField.id] || '')}
          onChange={handleValueChange}
          placeholder={expandedField.placeholder || 'Select or type...'}
          className="prompt-bar-inline-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
      );
    }

    if (expandedField.type === 'color') {
      const colorArray = Array.isArray(editedValues[expandedField.id]) 
        ? editedValues[expandedField.id] 
        : [];
      const hexPattern = /#[A-Fa-f0-9]{6}/g;
      const colors = colorArray.filter(v => hexPattern.test(String(v)));
      const textParts = colorArray.filter(v => !hexPattern.test(String(v))).map(v => String(v).trim()).filter(v => v);

      const hasColors = colors.length > 0;
      const hasText = textParts.length > 0;

      if (!hasColors && !hasText) {
        return <span className="input-placeholder">Pick colors...</span>;
      }

      // Simple text summary (like assets "2 images")
      let summary = '';
      if (hasColors) {
        summary = `${colors.length} color${colors.length > 1 ? 's' : ''}`;
      }
      if (hasText) {
        summary = summary ? `${summary} + text` : 'Text description';
      }

      return <span className="color-summary-text">{summary}</span>;
    }

    if (expandedField.type === 'dropdown') {
      return editedValues[expandedField.id] ? (
        <div className="value-chip">
          <span className="chip-text">{editedValues[expandedField.id]}</span>
          <button className="chip-remove" onClick={() => handleClearValue(expandedField.id)}>×</button>
        </div>
      ) : (
        <span className="input-placeholder">Select an option...</span>
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        id={expandedField.id}
        name={expandedField.id}
        value={Array.isArray(editedValues[expandedField.id])
          ? editedValues[expandedField.id].join(', ')
          : (editedValues[expandedField.id] || '')}
        onChange={handleValueChange}
        placeholder={expandedField.placeholder}
        className="prompt-bar-inline-input"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
    );
  };

  return (
    <>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmGenerate}
        onCancel={handleCancelGenerate}
        title="Confirm Generation"
        message="Are you sure you want to regenerate designs with these settings?"
      />

      <div className={`prompt-bar-container ${isExpanded ? 'expanded' : ''}`} ref={barRef}>
        {/* Options Panel */}
        {showOptionsPanel && (
          <div className="prompt-bar-options-panel">
            {renderOptionsPanel()}
          </div>
        )}

        {/* Main Bar Row */}
        <div className="prompt-bar-row">
          <div className={`prompt-bar ${isExpanded ? 'expanded' : ''}`}>
            {/* All Icons - Always in DOM */}
            <div className="prompt-bar-icons">
              {allFields.map((field, index) => {
                const isHovered = hoveredField === field.id;
                const isActive = expandedField?.id === field.id;
                const hasValue = field.id === 'assets'
                  ? editedValues.assets?.length > 0
                  : !!editedValues[field.id];
                const displayValue = getDisplayValue(field);

                // Calculate if this icon should be hidden (not the active one when expanded)
                const shouldHide = isExpanded && !isActive;

                // For tooltip - convert array to string
                const rawValue = Array.isArray(editedValues[field.id]) 
                  ? editedValues[field.id].join(', ') 
                  : String(editedValues[field.id] || '');
                const colorMatches = field.id === 'color' && Array.isArray(editedValues[field.id])
                  ? editedValues[field.id].filter(v => /#[A-Fa-f0-9]{6}/.test(String(v)))
                  : [];
                const colorText = field.id === 'color'
                  ? rawValue.replace(/#[A-Fa-f0-9]{6}/g, '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim()
                  : '';
                const hasContent = field.id === 'assets'
                  ? (editedValues.assets?.length > 0)
                  : field.id === 'color'
                    ? (colorMatches.length > 0 || !!colorText || !!displayValue)
                    : !!displayValue;

                return (
                  <div
                    key={field.id}
                    className={`prompt-bar-icon-wrapper ${hasValue ? 'has-value' : ''} ${isActive ? 'active' : ''} ${shouldHide ? 'hidden' : ''}`}
                    data-field-id={field.id}
                    onMouseEnter={() => !isExpanded && setHoveredField(field.id)}
                    onMouseLeave={() => setHoveredField(null)}
                    onClick={() => handleIconClick(field)}
                    style={{ '--icon-index': index }}
                  >
                    <img
                      src={getIcon(field.id, isHovered || isActive)}
                      alt={field.display}
                      className="prompt-bar-icon"
                    />

                    {/* Active dot under icon */}
                    {isActive && <span className="prompt-bar-active-dot" />}

                    {/* Tooltip - only when not expanded */}
                    {isHovered && !isExpanded && (
                      <div className="prompt-bar-tooltip">
                        <span className="tooltip-label">{field.display}</span>
                        {field.id === 'assets' ? (
                          <div className="tooltip-assets">
                            {hasContent ? (
                              <>
                                {editedValues.assets.slice(0, 4).map((asset, idx) => (
                                  <img
                                    key={idx}
                                    src={getAssetPreviewUrl(asset, idx)}
                                    alt={`Asset ${idx + 1}`}
                                    className="tooltip-asset-preview"
                                  />
                                ))}
                                {editedValues.assets.length > 4 && (
                                  <span className="tooltip-asset-more">+{editedValues.assets.length - 4}</span>
                                )}
                              </>
                            ) : (
                              <span className="tooltip-empty">No assets</span>
                            )}
                          </div>
                        ) : field.id === 'color' ? (
                          <div className="tooltip-colors">
                            {colorMatches.length > 0 && colorMatches.map((color, idx) => (
                              <span key={idx} className="tooltip-color-dot" style={{ backgroundColor: color }} />
                            ))}
                            {colorText && <span className="tooltip-text">{colorText}</span>}
                            {colorMatches.length === 0 && !colorText && <span className="tooltip-empty">None</span>}
                          </div>
                        ) : (
                          hasContent ? (
                            <span className="tooltip-text">{displayValue}</span>
                          ) : (
                            <span className="tooltip-empty">None</span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Field Label - Shows when expanded */}
            {isExpanded && (
              <span className="prompt-bar-field-label">{expandedField.display}</span>
            )}

            {/* Input Area - Shows when expanded */}
            <div className={`prompt-bar-input-area ${isExpanded ? 'visible' : ''}`}>
              {renderInputContent()}
            </div>

            {/* Confirm button */}
            <button
              className={`prompt-bar-confirm ${isExpanded ? 'visible' : ''}`}
              onClick={() => setExpandedField(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Generate button */}
          <button className="prompt-bar-generate" onClick={handleSubmit}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 4L12 20M12 4L6 10M12 4L18 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default PromptBar;
